import { useCallback, useEffect, useRef, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { useDock, useAllDockItems } from './DockContext';
import { DockItem } from './DockItem';
import type { DockItem as DockItemType } from '../../types/dock';
import './dock-drawer.css';

/**
 * Drawer component that slides up from the dock
 * Shows all items in a grid with edit/reorder functionality
 */
export function DockDrawer(): JSX.Element {
  const {
    isDrawerOpen,
    isEditing,
    closeDrawer,
    toggleEditMode,
    reorderItems,
    triggerAction,
  } = useDock();
  const allItems = useAllDockItems();
  const drawerRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Set up dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle clicks outside drawer to close
  useEffect(() => {
    if (!isDrawerOpen) {return;}

    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node;
      const drawer = drawerRef.current;
      const dock = document.querySelector('.dock');

      if (
        drawer &&
        !drawer.contains(target) &&
        dock &&
        !dock.contains(target)
      ) {
        closeDrawer();
      }
    };

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        closeDrawer();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isDrawerOpen, closeDrawer]);

  // Handle drag start
  const handleDragStart = useCallback((event: { active: { id: string | number } }) => {
    setActiveId(String(event.active.id));
  }, []);

  // Handle drag end
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (over && active.id !== over.id) {
        const oldIndex = allItems.findIndex((item) => item.id === active.id);
        const newIndex = allItems.findIndex((item) => item.id === over.id);

        if (oldIndex !== -1 && newIndex !== -1) {
          const newOrder = [...allItems];
          const [movedItem] = newOrder.splice(oldIndex, 1);
          newOrder.splice(newIndex, 0, movedItem);
          reorderItems(newOrder.map((item) => item.id));
        }
      }
    },
    [allItems, reorderItems]
  );

  const handleItemClick = useCallback(
    (action: string) => {
      triggerAction(action as Parameters<typeof triggerAction>[0]);
      closeDrawer();
    },
    [triggerAction, closeDrawer]
  );

  const handleEditToggle = useCallback(() => {
    toggleEditMode();
  }, [toggleEditMode]);

  // Find the active item for drag overlay
  const activeItem = activeId
    ? allItems.find((item) => item.id === activeId)
    : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`dock-drawer-backdrop ${isDrawerOpen ? 'dock-drawer-backdrop--visible' : ''}`}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        id="dock-drawer"
        className={`dock-drawer ${isDrawerOpen ? 'dock-drawer--open' : ''}`}
        role="dialog"
        aria-label="Dock drawer"
        aria-hidden={!isDrawerOpen}
      >
        {/* Header */}
        <div className="dock-drawer-header">
          <h2 className="dock-drawer-title">All Items</h2>
          <div className="dock-drawer-actions">
            <button
              type="button"
              className={`dock-drawer-button ${isEditing ? 'dock-drawer-button--active' : ''}`}
              onClick={handleEditToggle}
              aria-pressed={isEditing}
            >
              {isEditing ? 'Done' : 'Edit'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="dock-drawer-content">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={allItems.map((item) => item.id)}
              strategy={rectSortingStrategy}
            >
              <div className="dock-drawer-grid">
                {allItems.map((item) => (
                  <DockItem
                    key={item.id}
                    item={item}
                    onClick={() => handleItemClick(item.action)}
                    inDrawer
                    isDragEnabled={isEditing}
                  />
                ))}
              </div>
            </SortableContext>

            {/* Drag overlay for better visual feedback */}
            <DragOverlay>
              {activeItem ? (
                <DragOverlayItem item={activeItem} />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>
    </>
  );
}

/**
 * Drag overlay item component
 * Shown while dragging to provide visual feedback
 */
function DragOverlayItem({ item }: { item: DockItemType }): JSX.Element {
  return (
    <div className="dock-item dock-item--drawer dock-drag-overlay">
      <button type="button" className="dock-item-button" disabled>
        <span className="dock-item-icon" aria-hidden="true">
          {item.icon}
        </span>
      </button>
      <span className="dock-item-label">{item.label}</span>
    </div>
  );
}

export default DockDrawer;
